import { useCallback, useMemo, useState } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useBoolean } from 'usehooks-ts';
import {
	Toast,
	AddPlusIcon,
	Button,
	HeaderBackButton,
	LoadingOverlay,
	Text,
	Modal,
	spacing,
} from '@pletly/ui';

import { RootStackScreenProps } from '../../../../navigation/types';
import { useTeamForm, TeamFormActions, TeamForm } from '../../../team/components/TeamForm';
import { useCreateTeam } from '../../../team/new/components/useCreateTeam';
import { withTranslation } from '../../../../locales/hoc';
import { useOrganisationData } from '../../../view/components/useOrganisationData';
import { useAssignMemberToTeamsMutation } from './components/__generated__/assignTeams.mutation.generated';
import { GetOrganisationDocument } from '../../../view/components/__generated__/organisation.generated';
import { TeamSelectRow } from './components/TeamSelectRow';

type Props = RootStackScreenProps<'AssignMemberToTeams'>;

export type AssignMemberToTeamsScreenParams = {
	organisationUuid: string;
	memberId: string;
};

export const AssignMemberToTeams = withTranslation(
	({
		route: {
			params: { organisationUuid, memberId },
		},
		navigation: { goBack },
	}: Props) => {
		const { t } = useTranslation();
		const {
			value: isCreateTeamVisible,
			setTrue: showCreateTeamForm,
			setFalse: hideCreateTeamForm,
		} = useBoolean(false);

		const createTeam = useCreateTeam({ organisationUuid, onTeamCreated: hideCreateTeamForm });
		const teamFormMethods = useTeamForm();

		const { loading: organisationLoading, organisation } = useOrganisationData({
			uuid: organisationUuid,
		});

		const [assignTeams, { loading: assignLoading }] = useAssignMemberToTeamsMutation({
			refetchQueries: [GetOrganisationDocument],
		});

		const loading = createTeam.loading || organisationLoading || assignLoading;

		const initialSelectedUuids = useMemo(() => {
			if (!organisation) return [];

			return organisation.teams.reduce<string[]>((teamUuids, team) => {
				if (team.members.caregivers.some((caregiver) => caregiver.memberId === memberId)) {
					teamUuids.push(team.uuid);
				}

				return teamUuids;
			}, []);
		}, [memberId, organisation]);

		const [selectedUuids, setSelectedUuids] = useState(initialSelectedUuids);

		const handleTeamPress = useCallback(
			(teamUuid: string, isSelected: boolean) => {
				if (!isSelected) {
					setSelectedUuids([...selectedUuids, teamUuid]);
				} else {
					setSelectedUuids(selectedUuids.filter((uuid) => uuid !== teamUuid));
				}
			},
			[selectedUuids],
		);

		const handleSave = useCallback(async () => {
			try {
				await assignTeams({
					variables: {
						data: {
							memberId,
							teamsUuids: selectedUuids,
						},
					},
				});
				Toast.show({ text1: t('toasts.success.teams-assigned') });
				goBack();
			} catch (error) {
				console.error(error);
				Toast.show({ text1: t('toasts.error.something-wrong'), type: 'error' });
			}
		}, [assignTeams, goBack, memberId, selectedUuids, t]);

		const actions = useMemo(
			() => (
				<>
					<Button style={styles.button} onPress={goBack} color="primary">
						{t('common.cancel')}
					</Button>
					<Button style={styles.button} color="primary" variant="filled" onPress={handleSave}>
						{t('assign')}
					</Button>
				</>
			),
			[goBack, handleSave, t],
		);

		return (
			<Modal
				visible
				onClose={goBack}
				title={
					!isCreateTeamVisible ? (
						t('assign-to-teams')
					) : (
						<HeaderBackButton onPress={hideCreateTeamForm} />
					)
				}
				actions={
					!isCreateTeamVisible ? (
						actions
					) : (
						<TeamFormActions
							onCancel={hideCreateTeamForm}
							onSubmit={teamFormMethods.handleSubmit(createTeam.submit)}
						/>
					)
				}
				withOwnToast={false}
			>
				<LoadingOverlay show={loading} />
				{!isCreateTeamVisible ? (
					<View style={styles.teamsContainer}>
						<Pressable onPress={showCreateTeamForm} style={styles.createNewButton}>
							<AddPlusIcon />
							<Text category="p2">{t('common.create-new')}</Text>
						</Pressable>
						{organisation?.teams.map((team) => {
							const isSelected = selectedUuids.includes(team.uuid);

							return (
								<TeamSelectRow
									key={team.uuid}
									team={team}
									isSelected={isSelected}
									onPress={handleTeamPress}
								/>
							);
						})}
					</View>
				) : (
					<View style={styles.teamsContainer}>
						<Text category="h4">{t('teams.new-team')}</Text>
						<TeamForm formMethods={teamFormMethods} />
					</View>
				)}
			</Modal>
		);
	},
);

const styles = StyleSheet.create({
	teamsContainer: {
		gap: spacing(2),
	},
	createNewButton: {
		flexDirection: 'row',
		alignItems: 'center',
	},
	button: {
		flex: 1,
	},
});
